/**
 * Edge handling utilities for switchboard canvas
 */
import type { SwitchboardEdge } from '../../converters/shared/reactFlowTypes'
import {
  AUTO_GENERATED_EDGE_PREFIX,
  CUSTOM_HANDLE_PREFIXES,
  CUSTOM_HANDLE_TYPES_SET,
  type CustomHandleType,
  DAY_NIGHT_HANDLE_TYPES_SET,
  type DayNightHandleType,
  QUEUE_HANDLE_TYPES_SET,
  type QueueHandleType,
  SCHEDULE_HANDLE_TYPES_SET,
  type ScheduleHandleType,
  TRANSFER_AND_ROLLOVER_HANDLE_TYPES_SET,
  type TransferAndRolloverHandleType,
} from './constants'

/**
 * Create a unique edge key from source, target, and optional sourceHandle/targetHandle
 */
export function createEdgeKey(
  source: string,
  target: string,
  sourceHandle?: string | null,
  targetHandle?: string | null,
): string {
  const sourceHandlePart = sourceHandle ? `-${sourceHandle}` : ''
  const targetHandlePart = targetHandle ? `-${targetHandle}` : ''
  return `${source}${sourceHandlePart}-${target}${targetHandlePart}`
}

/**
 * Check if an edge ID is auto-generated by React Flow
 */
export function isAutoGeneratedEdge(edgeId: string): boolean {
  return edgeId.startsWith(AUTO_GENERATED_EDGE_PREFIX)
}

/**
 * Normalize handle value by converting sentinel strings to undefined
 */
function normalizeHandle(
  handle: string | null | undefined,
): string | undefined {
  if (!handle || handle === 'null' || handle === 'undefined') {
    return undefined
  }
  return handle
}

/**
 * Type guard: Check if a handle is a custom handle type (onInvalid, onTimeout)
 */
export function isCustomHandleType(
  sourceHandle: string | null | undefined,
): sourceHandle is CustomHandleType {
  const normalized = normalizeHandle(sourceHandle)
  return (
    normalized !== undefined &&
    CUSTOM_HANDLE_TYPES_SET.has(normalized as CustomHandleType)
  )
}

/**
 * Type guard: Check if a handle is a schedule handle type
 */
function isScheduleHandle(
  handle: string | null | undefined,
): handle is ScheduleHandleType {
  const normalized = normalizeHandle(handle)
  return (
    normalized !== undefined &&
    SCHEDULE_HANDLE_TYPES_SET.has(normalized as ScheduleHandleType)
  )
}

/**
 * Type guard: Check if a handle is a day/night handle type
 */
function isDayNightHandle(
  handle: string | null | undefined,
): handle is DayNightHandleType {
  const normalized = normalizeHandle(handle)
  return (
    normalized !== undefined &&
    DAY_NIGHT_HANDLE_TYPES_SET.has(normalized as DayNightHandleType)
  )
}

/**
 * Type guard: Check if a handle is a queue handle type
 */
function isQueueHandle(
  handle: string | null | undefined,
): handle is QueueHandleType {
  const normalized = normalizeHandle(handle)
  return (
    normalized !== undefined &&
    QUEUE_HANDLE_TYPES_SET.has(normalized as QueueHandleType)
  )
}

/**
 * Type guard: Check if a handle is a transfer and rollover handle type
 */
function isTransferAndRolloverHandle(
  handle: string | null | undefined,
): handle is TransferAndRolloverHandleType {
  const normalized = normalizeHandle(handle)
  return (
    normalized !== undefined &&
    TRANSFER_AND_ROLLOVER_HANDLE_TYPES_SET.has(
      normalized as TransferAndRolloverHandleType,
    )
  )
}

/**
 * Check if a sourceHandle is a custom handle (prefixes or specific types)
 * Uses type guards and Sets for type-safe checking
 */
export function isCustomSourceHandle(
  sourceHandle: string | null | undefined,
): boolean {
  const normalized = normalizeHandle(sourceHandle)
  if (!normalized) {
    return false
  }

  // Check for prefix matches
  if (CUSTOM_HANDLE_PREFIXES.some((prefix) => normalized.startsWith(prefix))) {
    return true
  }

  // Use type guards for specific handle types
  return (
    isScheduleHandle(normalized) ||
    isDayNightHandle(normalized) ||
    isQueueHandle(normalized) ||
    isTransferAndRolloverHandle(normalized)
  )
}

/**
 * Normalize edges by removing unnecessary handles and labels
 */
export function normalizeEdge(edge: SwitchboardEdge): SwitchboardEdge {
  return {
    ...edge,
    // Only preserve sourceHandle if it's a custom handle
    sourceHandle: isCustomSourceHandle(edge.sourceHandle)
      ? edge.sourceHandle
      : undefined,
    // Remove targetHandle so React Flow automatically uses side handles
    targetHandle: undefined,
    // Remove label to prevent "drag" from appearing on connection lines
    label: undefined,
  }
}

/**
 * Deduplicate edges, keeping the one with the most complete information
 */
export function deduplicateEdges(edges: SwitchboardEdge[]): SwitchboardEdge[] {
  const edgeMap = new Map<string, SwitchboardEdge>()

  edges.forEach((edge) => {
    const normalizedEdge = normalizeEdge(edge)
    const edgeKey = createEdgeKey(
      normalizedEdge.source,
      normalizedEdge.target,
      normalizedEdge.sourceHandle,
    )

    // Keep the edge with sourceHandle if we have a choice
    const existing = edgeMap.get(edgeKey)
    if (!existing || (normalizedEdge.sourceHandle && !existing.sourceHandle)) {
      edgeMap.set(edgeKey, normalizedEdge)
    }
  })

  return Array.from(edgeMap.values())
}

/**
 * Remove auto-generated edges if proper edges exist
 */
export function removeAutoGeneratedEdges(
  edges: SwitchboardEdge[],
): SwitchboardEdge[] {
  return edges.filter((edge) => {
    if (isAutoGeneratedEdge(edge.id)) {
      const properEdge = edges.find(
        (e) =>
          !isAutoGeneratedEdge(e.id) &&
          e.source === edge.source &&
          e.target === edge.target &&
          e.sourceHandle === edge.sourceHandle,
      )
      return !properEdge // Keep auto-generated only if no proper one exists
    }
    return true // Keep all proper edges
  })
}

/**
 * Restore missing sourceHandles from edge IDs if they contain handle information
 * This recovers handle data that may have been lost during edge creation
 */
function restoreSourceHandlesFromIds(
  edges: SwitchboardEdge[],
): SwitchboardEdge[] {
  return edges.map((edge) => {
    // If edge already has sourceHandle, no need to restore
    if (edge.sourceHandle) {
      return edge
    }

    // If edge ID contains sourceHandle info, try to restore it
    if (edge.id.includes('-')) {
      const parts = edge.id.split('-')
      // Check for patterns like: source-onInvalid-target or source-onTimeout-target
      for (let i = 0; i < parts.length; i++) {
        if (isCustomHandleType(parts[i])) {
          return { ...edge, sourceHandle: parts[i] }
        }
        // Also check for option-{index} pattern
        if (parts[i] === 'option' && i + 1 < parts.length) {
          const optionId = `${parts[i]}-${parts[i + 1]}`
          return { ...edge, sourceHandle: optionId }
        }
      }
    }

    return edge
  })
}

/**
 * Clean edges: restore handles, normalize, deduplicate, and remove auto-generated ones
 * This is the single source of truth for all edge cleaning logic
 */
export function cleanEdges(edges: SwitchboardEdge[]): SwitchboardEdge[] {
  // First, restore any missing sourceHandles from edge IDs
  const withRestoredHandles = restoreSourceHandlesFromIds(edges)
  // Then normalize, deduplicate, and remove auto-generated edges
  const normalized = withRestoredHandles.map(normalizeEdge)
  const deduplicated = deduplicateEdges(normalized)
  return removeAutoGeneratedEdges(deduplicated)
}
